import { FC, useEffect } from "react";
import "./App.scss";
import { BrowserRouter, Redirect, Route, Switch, useLocation } from "react-router-dom";
import Env from "./utils/env";
import AlertProvider from "./components/shared/AlertComponent/AlertContext";
import AlertComponent from "./components/shared/AlertComponent/AlertComponent";
import { RedirectToOnboardingPage } from "./utils/redirect";
import { Logger } from "./utils/logger";
import { Bullseye, Spinner } from "@patternfly/react-core";
import * as Sentry from "@sentry/react";
import { getUserInfoFromJWT } from "./utils/fetch";
import { getCwfConfig } from "./api/hydra";
import { RHCAuthProvider, useRHCAuthContext } from "@rhc-shared-components/rhc-sso";
import { ControllerContextProvider } from "@rhc-shared-components/control-wrapper";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CertWorkflowApp } from "./components/CertWorkflowApp/CertWorkflowApp";
import { checkAndSendAnalytics } from '@rhc-shared-components/rhc-eddl';
import { CWRouteParams } from "./constants/router";
import { RHCErrorPage } from '@rhc-shared-components/error-page';
import { useOnboardingStatus } from "./queries/general";

declare global {
  interface Window {
    "__TANSTACK_QUERY_CLIENT__":
      import("@tanstack/query-core").QueryClient;
  }
}


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000, // 10 Minutes
    }
  }
});

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

const PLSRedirectComponent = ({url}:{url: string}) => {
    window.location.href = url;
    return (
        <div className="redirect-text">
            <Bullseye>
                Redirecting to the new url, please update your bookmarks...
            </Bullseye>
        </div>
    );
};
const AuthenticatedApp: FC = () => {
    const { isAuthenticated } = useRHCAuthContext();
    const { pathname } = useLocation<CWRouteParams>();
    const {
        data: onboardingStatus,
        isLoading,
        error,
    } = useOnboardingStatus(isAuthenticated);

    useEffect(() => {
        if (!onboardingStatus) return;

        Logger("User is authenticated");
        if (onboardingStatus && onboardingStatus["status"] === "completed") {
            Logger("User is onboarded, enabling the app");
        } else {
            Logger("User is not onboarded, redirecting to tnc page");
            RedirectToOnboardingPage();
        }
    }, [onboardingStatus]);

    useEffect(() => {
        if (isAuthenticated && Env.getEnvName() === Env.EnvNames.PROD) {
            Sentry.setUser(getUserInfoFromJWT());
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            checkAndSendAnalytics({ spaName: 'certification-workflow' });
        }
    }, [isAuthenticated, pathname]);

    if (error) {
        return (
            <div className="app">
                <RHCErrorPage errorStatus={error?.status} />
            </div>
        );
    }
    return (
        <div className="app">
            {(isLoading || !isAuthenticated) ?
                <Bullseye><Spinner/></Bullseye> :
                <ControllerContextProvider appKey={'cwf_config'} configApi={getCwfConfig}>
                    <span className="main-app">
                        <AlertComponent/>
                        <Switch>
                          {
                            Env.isDevEnv ?
                              <Redirect from={Env.routePrefix}
                                        to={Env.routePrefix + "/view/6508136a5bc646e3a7a22367/images"} exact />
                              : <Route
                                path={Env.routePrefix}
                                render={() => (<PLSRedirectComponent url={Env.productListingUrl + "/components"} />)}
                                exact={true}
                              />
                          }
                            <Route
                              path={Env.routePrefix + "/create/:project_type"}
                              render={() => (<PLSRedirectComponent url={Env.productListingUrl + "/products"} />)}
                              exact={true}
                            />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/overview`}
                                      to={`${Env.routePrefix}/view/:oldProductId`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/images`}
                                      to={`${Env.routePrefix}/view/:oldProductId/images`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/settings`}
                                      to={`${Env.routePrefix}/view/:oldProductId/repository-info`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/test-results`}
                                      to={`${Env.routePrefix}/view/:oldProductId`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/update-graph`}
                                      to={`${Env.routePrefix}/view/:oldProductId/update-graph`} exact />
                            <Route
                              path={`${Env.oldRoutePrefix}`}
                              render={props => (<Redirect
                                to={`${props?.location?.pathname?.replace(Env.oldRoutePrefix, Env.routePrefix)}`} />)}
                            />
                            <Route
                              path={`${Env.routePrefix}/:productId/:id/`}
                              component={CertWorkflowApp}
                              exact={false}
                            />
                            <Route
                              render={() => (
                                <RHCErrorPage errorStatus={404} />
                              )}
                            />
                        </Switch>
                        <div id="modal-div-container"/>
                    </span>
                </ControllerContextProvider>}
        </div>
    );
};

const App: FC = () => (<QueryClientProvider client={queryClient}>
    <RHCAuthProvider environment={"STAGE"}>
        <AlertProvider>
            <BrowserRouter>
                <AuthenticatedApp/>
            </BrowserRouter>
        </AlertProvider>
    </RHCAuthProvider>
</QueryClientProvider>)

export default App;
